import { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import { Send, Copy, Mail, Check, Users } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ShareDialog({ recommendationId, triggerLabel }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const ensureLink = async () => {
    if (link) return link;
    setBusy(true);
    try {
      const { data } = await api.post(`/recommendations/${recommendationId}/share`, {
        message,
      });
      setLink(data.link);
      return data.link;
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!recipient) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/recommendations/${recommendationId}/share`, {
        recipient_email: recipient.trim().toLowerCase(),
        message,
      });
      setLink(data.link);
      setEmailSent(true);
      toast.success(t("share_email_sent"));
    } catch {
      toast.error(t("err_generic"));
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = async () => {
    const l = await ensureLink();
    const text =
      (message ||
        (lang === "fr"
          ? "Coucou, j'utilise Sereno pour commencer à épargner. Voici ma recommandation."
          : "Hi, I'm using Sereno to start saving. Here's my recommendation.")) +
      "\n\n" +
      l;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    if (typeof window !== "undefined") window.open(url, "_blank");
  };

  const copyLink = async () => {
    const l = await ensureLink();
    try {
      await navigator.clipboard.writeText(l);
      setLinkCopied(true);
      toast.success(t("share_link_copied"));
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error(t("err_generic"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="share-open-btn"
          variant="outline"
          className="min-h-[52px] rounded-full border-2 border-[#101A42] text-base font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
        >
          <Users className="mr-2 h-5 w-5" />
          {triggerLabel || t("share_family")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-lg bg-white"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#101A42]">
            {t("share_title")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-base leading-relaxed text-[#4A4A4A]">{t("share_intro")}</p>

        <Tabs defaultValue="email" className="mt-4">
          <TabsList className="grid w-full grid-cols-3 h-12 bg-[#F8EFD9]">
            <TabsTrigger value="email" data-testid="share-tab-email" className="text-base">
              <Mail className="mr-1 h-4 w-4" />
              {t("share_email_tab")}
            </TabsTrigger>
            <TabsTrigger
              value="whatsapp"
              data-testid="share-tab-whatsapp"
              className="text-base"
            >
              {t("share_whatsapp_tab")}
            </TabsTrigger>
            <TabsTrigger value="link" data-testid="share-tab-link" className="text-base">
              {t("share_link_tab")}
            </TabsTrigger>
          </TabsList>

          <div className="mt-5">
            <Label className="mb-2 block text-base font-medium">
              {t("share_message_label")}
            </Label>
            <Textarea
              data-testid="share-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("share_message_placeholder")}
              rows={3}
              className="rounded-xl border-2 text-base"
            />
          </div>

          <TabsContent value="email" className="mt-5 space-y-4">
            <div>
              <Label htmlFor="share-recipient" className="mb-2 block text-base font-medium">
                {t("share_recipient_email")}
              </Label>
              <Input
                id="share-recipient"
                data-testid="share-recipient-input"
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="h-14 rounded-xl border-2 text-base"
              />
            </div>
            <Button
              onClick={sendEmail}
              disabled={busy || !recipient}
              data-testid="share-send-email-btn"
              className="min-h-[56px] w-full rounded-full bg-[#A762B1] text-lg font-semibold text-white hover:bg-[#B775C0]"
            >
              {emailSent ? (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  {t("share_email_sent")}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  {t("share_send_email")}
                </>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-5">
            <Button
              onClick={openWhatsApp}
              disabled={busy}
              data-testid="share-whatsapp-btn"
              className="min-h-[56px] w-full rounded-full bg-[#25D366] text-lg font-semibold text-white hover:bg-[#1CB758]"
            >
              {t("share_open_whatsapp")}
            </Button>
          </TabsContent>

          <TabsContent value="link" className="mt-5 space-y-3">
            <Button
              onClick={copyLink}
              disabled={busy}
              data-testid="share-copy-link-btn"
              className="min-h-[56px] w-full rounded-full bg-[#101A42] text-lg font-semibold text-white hover:bg-[#1A2650]"
            >
              {linkCopied ? (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  {t("share_link_copied")}
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-5 w-5" />
                  {t("share_copy_link")}
                </>
              )}
            </Button>
            {link && (
              <p
                data-testid="share-link-preview"
                className="break-all rounded-xl border border-[#E8DFC7] bg-[#FFF7E9]/60 p-3 text-sm text-[#101A42]"
              >
                {link}
              </p>
            )}
            <p className="text-sm text-[#4A4A4A]">{t("share_link_valid")}</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
